import React, { useState } from 'react';
import {
    FaWhatsapp,
    FaGift,
    FaMoneyBillWave,
    FaUniversity,
    FaMobileAlt,
} from 'react-icons/fa';
import './Order.css';
import { useSettings } from '../context/SettingsContext';

const ICON_COLOR = '#6B1A1A';

export default function Order({ cart, removeFromCart, updateQty }) {
    const SITE_CONFIG = useSettings();
    const DELIVERY_FEE = SITE_CONFIG.deliveryFee ?? 200;
    const [form, setForm] = useState({
        name: '', phone: '', address: '', note: '', paymentMethod: '',
    });

    const [isGift, setIsGift] = useState(false);
    const [occasion, setOccasion] = useState('');
    const [giftForm, setGiftForm] = useState({
        receiverName: '', receiverPhone: '', receiverAddress: '', message: '',
    });

    const [sent, setSent] = useState(false);

    const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
    const freeThreshold = SITE_CONFIG.freeDeliveryAbove || 0;
    const deliveryCharge = freeThreshold > 0 && subtotal >= freeThreshold ? 0 : DELIVERY_FEE;
    const total = subtotal + deliveryCharge;

    const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });
    const handleGiftChange = e => setGiftForm({ ...giftForm, [e.target.name]: e.target.value });

    const handleWhatsApp = () => {
        if (!cart.length) return;

        const items = cart
            .map(i => {
                const poundStr = i.selectedPound ? ` (${i.selectedPound} lbs)` : '';
                const flavourStr = i.selectedFlavour ? ` [${i.selectedFlavour}]` : '';
                return `• ${i.name}${poundStr}${flavourStr} x${i.qty} — PKR ${(i.price * i.qty).toLocaleString()}`;
            })
            .join('%0A');

        const giftDetails = isGift
            ? `%0A%0A*Gift / Special Occasion Order*%0AOccasion: ${occasion || 'Not specified'}%0AReceiver Name: ${giftForm.receiverName}%0AReceiver Phone: ${giftForm.receiverPhone}%0ADelivery Address / Venue: ${giftForm.receiverAddress}%0AGift Message: ${giftForm.message || 'None'}`
            : '';

        const msg =
            `Hello ${SITE_CONFIG.brandName}!%0A%0A*New Order*%0A%0A${items}` +
            `%0A%0A*Subtotal:* PKR ${subtotal.toLocaleString()}` +
            `%0A*Delivery:* PKR ${deliveryCharge.toLocaleString()}` +
            `%0A*Total:* PKR ${total.toLocaleString()}` +
            `%0A*Payment Method:* ${form.paymentMethod || 'Not specified'}` +
            giftDetails +
            `%0A%0A*Sender Name:* ${form.name}` +
            `%0A*Phone:* ${form.phone}` +
            `%0A*Address:* ${form.address}` +
            `%0A*Note:* ${form.note || 'None'}`;

        window.open(`https://wa.me/${SITE_CONFIG.whatsapp}?text=${msg}`, '_blank');
        setSent(true);
    };

    // Payment method options with icons
    const PAYMENT_OPTIONS = [
        { value: 'Cash on Delivery', label: 'Cash on Delivery', Icon: FaMoneyBillWave },
        { value: 'Bank Transfer', label: 'Online Bank Transfer', Icon: FaUniversity },
        { value: 'JazzCash/EasyPaisa', label: 'JazzCash / EasyPaisa', Icon: FaMobileAlt },
    ];

    return (
        <div className="order-page">
            <div className="order-hero">
                <p className="section-subtitle">Your Selection</p>
                <h1 className="section-title">My Order</h1>
                <div className="gold-rule" style={{ maxWidth: 280, margin: '16px auto 0' }} />
            </div>

            <div className="order-layout">

                {/* ── LEFT — Cart ── */}
                <div className="cart-section">
                    <h2 className="cart-heading">Order Summary</h2>

                    {cart.length === 0 ? (
                        <div className="cart-empty">
                            <FaGift size={44} color={ICON_COLOR} style={{ marginBottom: 14 }} />
                            <p>Your order is empty.</p>
                            <a href="/" className="btn-primary" style={{ display: 'inline-block', marginTop: 16 }}>
                                Browse Cakes
                            </a>
                        </div>
                    ) : (
                        <>
                            {cart.map(item => (
                                <div className="cart-item" key={item.id}>
                                    <img src={item.image} alt={item.name} className="cart-item-img" />
                                    <div className="cart-item-info">
                                        <p className="cart-item-cat">{item.category}</p>
                                        <h3 className="cart-item-name">{item.name}</h3>
                                        {item.selectedPound && <p style={{ fontSize: '12px', color: 'var(--text-mid)', marginTop: '-4px' }}>Size: {item.selectedPound} lbs</p>}
                                        {item.selectedFlavour && <p style={{ fontSize: '12px', color: 'var(--text-mid)', marginTop: '-2px', marginBottom: '4px' }}>Flavour: {item.selectedFlavour}</p>}
                                        <p className="cart-item-price">PKR {item.price.toLocaleString()} each</p>
                                    </div>
                                    <div className="cart-item-controls">
                                        <div className="qty-row">
                                            <button onClick={() => updateQty(item.id, item.qty - 1)}>−</button>
                                            <span>{item.qty}</span>
                                            <button onClick={() => updateQty(item.id, item.qty + 1)}>+</button>
                                        </div>
                                        <p className="cart-item-subtotal">PKR {(item.price * item.qty).toLocaleString()}</p>
                                        <button className="cart-remove" onClick={() => removeFromCart(item.id)}>Remove</button>
                                    </div>
                                </div>
                            ))}

                            <div className="cart-total">
                                <div className="gold-rule" />
                                <div className="total-row total-sub-row">
                                    <span>Subtotal</span>
                                    <span>PKR {subtotal.toLocaleString()}</span>
                                </div>
                                <div className="total-row total-sub-row">
                                    <span>Delivery Charges</span>
                                    <span>PKR {deliveryCharge.toLocaleString()}*</span>
                                </div>
                            
                                <p className="delivery-note">
                                    *Delivery charges are tentative and may vary depending on your area — final charges will be confirmed on WhatsApp.
                                </p>
                                <div className="total-row total-final-row">
                                    <span>Total</span>
                                    <span className="total-amount">PKR {total.toLocaleString()}</span>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* ── RIGHT — Form ── */}
                <div className="form-section">
                    <h2 className="cart-heading">Your Details</h2>

                    <div className="form-group">
                        <label>Full Name</label>
                        <input name="name" placeholder="Your name" value={form.name} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                        <label>Phone Number</label>
                        <input name="phone" placeholder="+92 3XX XXXXXXX" value={form.phone} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                        <label>Delivery Address</label>
                        <input name="address" placeholder="Area, Karachi" value={form.address} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                        <label>Special Note (optional)</label>
                        <textarea
                            name="note"
                            placeholder="Any custom message, flavour, or design request..."
                            value={form.note}
                            onChange={handleChange}
                            rows={3}
                        />
                    </div>

                    {/* ── PAYMENT METHOD ── */}
                    <div className="form-group">
                        <label>Payment Method</label>
                        <div className="payment-method-group">
                            {PAYMENT_OPTIONS.map(({ value, label, Icon }) => (
                                <label
                                    key={value}
                                    className={`payment-method-option ${form.paymentMethod === value ? 'selected' : ''}`}
                                >
                                    <input
                                        type="radio"
                                        name="paymentMethod"
                                        value={value}
                                        checked={form.paymentMethod === value}
                                        onChange={handleChange}
                                    />
                                    <Icon size={16} color={form.paymentMethod === value ? '#fff' : ICON_COLOR} />
                                    <span>{label}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* ── GIFT / OCCASION TOGGLE ── */}
                    <div className="gift-divider">
                        <label className="gift-toggle-row">
                            <input
                                type="checkbox"
                                checked={isGift}
                                onChange={e => setIsGift(e.target.checked)}
                            />
                            <FaGift size={14} color={ICON_COLOR} />
                            <span>Ordering as a Gift or Special Occasion?</span>
                        </label>
                    </div>

                    {isGift && (
                        <div className="gift-fields-block">
                            <div className="form-group">
                                <label>Occasion</label>
                                <select
                                    value={occasion}
                                    onChange={e => setOccasion(e.target.value)}
                                    className="form-select"
                                >
                                    <option value="">Select occasion</option>
                                    <option value="Birthday">Birthday</option>
                                    <option value="Wedding">Wedding</option>
                                    <option value="Anniversary">Anniversary</option>
                                    <option value="Graduation">Graduation</option>
                                    <option value="Bridal Shower">Bridal Shower</option>
                                    <option value="Baby Shower">Baby Shower</option>
                                    <option value="Corporate Event">Corporate Event</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Receiver's Name</label>
                                <input
                                    name="receiverName"
                                    placeholder="Full name of the receiver"
                                    value={giftForm.receiverName}
                                    onChange={handleGiftChange}
                                />
                            </div>
                            <div className="form-group">
                                <label>Receiver's Phone</label>
                                <input
                                    name="receiverPhone"
                                    placeholder="+92 3XX XXXXXXX"
                                    value={giftForm.receiverPhone}
                                    onChange={handleGiftChange}
                                />
                            </div>
                            <div className="form-group">
                                <label>Delivery Address / Venue</label>
                                <input
                                    name="receiverAddress"
                                    placeholder="Home address, wedding hall, or venue name"
                                    value={giftForm.receiverAddress}
                                    onChange={handleGiftChange}
                                />
                            </div>
                            <div className="form-group">
                                <label>Gift Message (optional)</label>
                                <textarea
                                    name="message"
                                    rows={3}
                                    placeholder="Write a message to include on the gift card..."
                                    value={giftForm.message}
                                    onChange={handleGiftChange}
                                />
                            </div>
                        </div>
                    )}

                    {/* ── SUBMIT ── */}
                    {sent ? (
                        <div className="order-sent">
                            Order sent via WhatsApp! We will confirm shortly.
                        </div>
                    ) : (
                        <button
                            className="whatsapp-order-btn"
                            onClick={handleWhatsApp}
                            disabled={!cart.length}
                        >
                            <FaWhatsapp size={18} color="#fff" style={{ marginRight: 8 }} />
                            Send Order via WhatsApp
                        </button>
                    )}
                </div>

            </div>
        </div>
    );
}
